/**
 * @fileoverview Inline chat file card for an agent-authored spreadsheet.
 * Builds CSV / XLSX client-side from the spec and triggers a download.
 */
import { useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { BookmarkPlus, Check, Download, FileSpreadsheet } from "lucide-react";
import type { SpreadsheetSpec } from "@cvx/lib/spreadsheets/spec";
import { buildWorkbook, firstSheetToCsv } from "@/utils/export/specToWorkbook";

export function SpreadsheetCard({
  spec,
  onSaveToCanvas,
}: {
  spec: SpreadsheetSpec;
  onSaveToCanvas?: (spec: SpreadsheetSpec) => Promise<void> | void;
}) {
  const [saved, setSaved] = useState(false);
  const rows = spec.sheets.reduce((n, s) => n + s.rows.length, 0);
  const cols = spec.sheets[0]?.columns.length ?? 0;

  const downloadXlsx = () => {
    const wb = buildWorkbook(spec);
    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([out], { type: "application/octet-stream" }),
      `${spec.filename}.xlsx`,
    );
  };
  const downloadCsv = () => {
    saveAs(
      new Blob([firstSheetToCsv(spec)], { type: "text/csv;charset=utf-8" }),
      `${spec.filename}.csv`,
    );
  };

  return (
    <div className="not-prose my-3 flex items-center justify-between gap-3 rounded-lg border border-border bg-background p-3 shadow-sm">
      <div className="flex min-w-0 items-center gap-2.5">
        <FileSpreadsheet className="h-5 w-5 shrink-0 text-emerald-700" />
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-foreground">
            {spec.filename}
          </p>
          <p className="text-xs text-muted-foreground">
            {spec.sheets.length} sheet{spec.sheets.length > 1 ? "s" : ""} ·{" "}
            {rows} rows · {cols} cols
          </p>
        </div>
      </div>
      <div className="flex shrink-0 gap-1.5">
        <button
          type="button"
          onClick={downloadCsv}
          className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-accent/20"
        >
          <Download className="h-3 w-3" /> CSV
        </button>
        <button
          type="button"
          onClick={downloadXlsx}
          className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-accent/20"
        >
          <Download className="h-3 w-3" /> XLSX
        </button>
        {onSaveToCanvas && (
          <button
            type="button"
            disabled={saved}
            onClick={async () => {
              try {
                await onSaveToCanvas(spec);
                setSaved(true);
              } catch {
                // The save handler surfaces its own error toast.
              }
            }}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-accent/20 disabled:opacity-60"
          >
            {saved ? (
              <Check className="h-3 w-3" />
            ) : (
              <BookmarkPlus className="h-3 w-3" />
            )}
            {saved ? "Saved" : "Save to canvas"}
          </button>
        )}
      </div>
    </div>
  );
}
